/**
 * Pure functions for Konobox hardware telemetry status calculation and Pacific Time formatting.
 * Single source of truth for the status engine (KAN-164 / Ticket 2).
 */

export interface DeviceStatusResult {
  overallStatus: 'online' | 'warning' | 'offline';
  isOnline: boolean;
  lastPingedDate: Date | null;
  cardOk: boolean;
  billOk: boolean;
  useCard: boolean;
  useCash: boolean;
}

const pacificFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short'
});

/**
 * Normalizes ping interval into milliseconds.
 * - Non-numeric, NaN, <= 0, or unset values default to 15 minutes (15 * 60 * 1000 ms).
 * - Values <= 120 are treated as minutes (e.g. 10 -> 600,000 ms; 0.5 -> 30,000 ms).
 * - Values > 120 are treated as milliseconds.
 */
export function normalizePingIntervalMs(interval: any): number {
  if (typeof interval !== 'number' || isNaN(interval) || interval <= 0) {
    return 15 * 60 * 1000;
  }
  if (interval <= 120) {
    return interval * 60 * 1000;
  }
  return interval;
}

/**
 * Parses raw `lastSeen` values across supported types:
 * - Firestore Timestamp (with .toDate() method or { seconds, nanoseconds } fields)
 * - Date instance
 * - Epoch milliseconds number (e.g. 1784143800000)
 * - ISO date string
 * Returns null for missing, null, empty, or unparsable values.
 */
export function parseLastSeenDate(lastSeen: any): Date | null {
  if (lastSeen === null || lastSeen === undefined || lastSeen === '') {
    return null;
  }
  if (typeof lastSeen.toDate === 'function') {
    const d = lastSeen.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (lastSeen instanceof Date) {
    return !isNaN(lastSeen.getTime()) ? lastSeen : null;
  }
  if (typeof lastSeen === 'object' && 'seconds' in lastSeen && typeof lastSeen.seconds === 'number') {
    const ms = lastSeen.seconds * 1000 + (lastSeen.nanoseconds || 0) / 1000000;
    const d = new Date(ms);
    return !isNaN(d.getTime()) ? d : null;
  }
  if (typeof lastSeen === 'number') {
    const d = new Date(lastSeen);
    return !isNaN(d.getTime()) ? d : null;
  }
  if (typeof lastSeen === 'string') {
    const trimmed = lastSeen.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    return !isNaN(d.getTime()) ? d : null;
  }
  return null;
}

/**
 * Formats a raw timestamp in Pacific Time (America/Los_Angeles):
 * Format: `M/d/yy, h:mm a z` (e.g. `7/15/26, 12:30 PM PDT` or `1/2/26, 12:00 PM PST`).
 * Returns 'Never' if unparsable or missing.
 */
export function formatLastSeenPacific(lastSeen: any): string {
  const date = parseLastSeenDate(lastSeen);
  if (!date) {
    return 'Never';
  }
  return pacificFormatter.format(date);
}

/**
 * Deterministic comparison for sorting devices by `lastSeen`.
 * - 'asc': 'Never' sorts oldest (first), followed by oldest to newest timestamp.
 * - 'desc': newest to oldest timestamp, followed by 'Never' at the bottom (last).
 * - Ties are broken by `deviceId` ascending.
 */
export function compareLastSeen(a: any, b: any, direction: 'asc' | 'desc' = 'asc'): number {
  const dateA = a.lastPingedDate ?? parseLastSeenDate(a.lastSeen);
  const dateB = b.lastPingedDate ?? parseLastSeenDate(b.lastSeen);
  const timeA = dateA ? dateA.getTime() : null;
  const timeB = dateB ? dateB.getTime() : null;

  const idA = a.deviceId || '';
  const idB = b.deviceId || '';

  if (timeA === null && timeB === null) {
    return idA.localeCompare(idB);
  }
  if (timeA === null) {
    return direction === 'asc' ? -1 : 1;
  }
  if (timeB === null) {
    return direction === 'asc' ? 1 : -1;
  }
  if (timeA !== timeB) {
    return direction === 'asc' ? timeA - timeB : timeB - timeA;
  }
  return idA.localeCompare(idB);
}

/**
 * Pure evaluation function for device online/warning/offline status (AC-B1..B4, AC-B7).
 * Used across initial load, snapshot listener, and background timer tick.
 *
 * Rules:
 * 1. Offline:
 *    - lastSeen is missing/unparsable, OR
 *    - device.deviceOnline === false, OR
 *    - age > pingIntervalMs (boundary is inclusive: age <= interval is online).
 *    Future lastSeen timestamps (negative age) are clamped to 0 to tolerate clock skew.
 * 2. Warning:
 *    - Machine is online, BUT one or more enabled payment peripherals report disconnected:
 *      (useCard !== false AND cardMachineConnected !== true) OR
 *      (useCash !== false AND billAcceptorConnected !== true).
 * 3. Online:
 *    - Machine is online AND all enabled peripherals report connected.
 */
export function computeStatus(
  device: any,
  nowMs: number,
  pingIntervalMs?: number
): DeviceStatusResult {
  const effectivePingIntervalMs = normalizePingIntervalMs(pingIntervalMs);
  const lastPingedDate = parseLastSeenDate(device?.lastSeen);

  let isOnline = false;
  if (lastPingedDate !== null) {
    const rawAgeMs = nowMs - lastPingedDate.getTime();
    const effectiveAgeMs = Math.max(0, rawAgeMs);
    isOnline = effectiveAgeMs <= effectivePingIntervalMs;
    // Explicit deviceOnline: false flag from telemetry overrides freshness
    if (device?.deviceOnline === false) {
      isOnline = false;
    }
  }

  const useCard = device?.useCard !== false;
  const useCash = device?.useCash !== false;
  const cardOk = !useCard || device?.cardMachineConnected === true;
  const billOk = !useCash || device?.billAcceptorConnected === true;

  let overallStatus: 'online' | 'warning' | 'offline' = 'online';
  if (!isOnline) {
    overallStatus = 'offline';
  } else if (!cardOk || !billOk) {
    overallStatus = 'warning';
  } else {
    overallStatus = 'online';
  }

  return {
    overallStatus,
    isOnline,
    lastPingedDate,
    cardOk,
    billOk,
    useCard,
    useCash
  };
}

